import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SkillsMultiSelect from '../../components/SkillsMultiSelect';
import CKEditorComponent from '../../components/CKEditorComponent';
import TextInput from '../../components/TextInput';
import FileInput from '../../components/FileInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import projectApi from '../../api/projectApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    excerpt: '',
    description: '',
    image: undefined,
    is_active: 0,
    skills: [],
  });
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [projectFound, setProjectFound] = useState(true);

  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const projectResponse = await projectApi.getProjectByIdentifier(id);
        if (!projectResponse.success || !projectResponse.data) {
          setProjectFound(false);
          setGeneralError(projectResponse.message || 'Project not found.');
          setLoading(false);
          return;
        }
        const projectToEdit = projectResponse.data;

        const skillsResponse = await projectApi.getProjectSkills(id);
        let projectSkills = [];
        if (skillsResponse.success && skillsResponse.data) {
          projectSkills = skillsResponse.data.map(skill => skill.id);
        } else {
          addToast('Failed to load skills for this project.', 'error');
        }

        setFormData({
          name: projectToEdit.name,
          excerpt: projectToEdit.excerpt,
          description: projectToEdit.description,
          image: undefined,
          is_active: projectToEdit.is_active ? 1 : 0,
          skills: projectSkills,
        });
        setCurrentImageUrl(projectToEdit.image);
        setProjectFound(true);

      } catch (err) {
        console.error('Failed to fetch project or skills:', err);
        setProjectFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load project. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
    if (validationErrors.description) {
      setValidationErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  const handleSkillChange = (selectedOptions) => {
    setFormData({ ...formData, skills: selectedOptions ? selectedOptions.map(option => option.value) : [] });
    if (validationErrors.skills) {
      setValidationErrors(prev => ({ ...prev, skills: undefined }));
    }
  };

  const handleImageRemove = () => {
    setFormData({ ...formData, image: null });
    setCurrentImageUrl(null);
    if (validationErrors.image) {
      setValidationErrors(prev => ({ ...prev, image: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'skills') {
        formData[key].forEach(skillId => data.append('skills[]', skillId));
      } else if (key === 'image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        } else if (formData[key] === null) {
          data.append(key, '');
        }
      } else {
        data.append(key, formData[key]);
      }
    }
    data.append('_method', 'PUT');

    try {
      await projectApi.updateProject(id, data);
      addToast('Project updated successfully!', 'success');
      navigate(ROUTES.PROJECTS);
    } catch (err) {
      console.error('Failed to update project:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update project. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!projectFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Project Not Found</h1>
        <p className="text-text-gray">{generalError || 'The project you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.PROJECTS)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Projects List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Project</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Project Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Project Name"
          error={validationErrors?.name?.[0]}
        />
        <TextInput
          label="Excerpt"
          name="excerpt"
          id="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          required={true}
          placeholder="Short description or summary"
          error={validationErrors?.excerpt?.[0]}
        />
        <CKEditorComponent
          label="Description"
          value={formData.description}
          onChange={handleDescriptionChange}
          required={true}
          error={validationErrors?.description?.[0]}
        />
        
        <SkillsMultiSelect
          label="Skills/Technologies"
          value={formData.skills}
          onChange={handleSkillChange}
          error={validationErrors?.skills?.[0]}
          required={true}
        />

        <FileInput
          label="Project Image"
          name="image"
          id="image"
          onChange={handleChange}
          error={validationErrors?.image?.[0]}
          currentFileMessage={currentImageUrl ? (
            <div className="flex items-center space-x-2">
              <span>Current Image:</span>
              <a href={currentImageUrl} target="_blank" rel="noopener noreferrer" className="text-text-white hover:underline">View</a>
              <Button
                type="button"
                onClick={handleImageRemove}
                className="text-red-500 hover:text-red-600 text-sm"
              >
                Remove
              </Button>
            </div>
          ) : (formData.image === null ? "No image selected" : "No current file")}
        />
        <CheckboxInput
          label="Active"
          name="is_active"
          id="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Project
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.PROJECTS)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditProject;